import {
  getBody,
  pageComponentType,
  updateComponentRenderInComponentMap,
  isComponent,
} from '@packages/utilities/platform';
import {
  Component,
  matchExpression,
  Plugins,
  Types,
  Expressions,
  withSystem,
  getSystem,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  EElement,
  EList,
  EText,
  EFunction,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {getPluginName, qualify} from '@packages/systems/wfdl-Qualified';
import {type TranslatedNodeData} from '@packages/systems/localization-types';
import {
  concat,
  emptyArray,
  constant,
  identity,
} from '@packages/utilities/fp/utils';
import {
  nodePojosToElements,
  getFieldDefaultExpression,
} from '@packages/systems/core/component-conversions';
import {isGuid, generateGuid} from '@packages/utilities/guid';
import {splitTextIntoElementsWithLineBreaks} from '@packages/systems/translation/utils';
import {parseTextWithTags} from './parseTextWithTags';
import {buildChildrenFromTokens} from './buildChildrenFromTokens';

type OverrideData = {
  element: ExpressionElement;
  componentPropertyId: string;
  defaultValue: Expression;
};

export const findSelectedElementComponent = (
  componentMap: Component.ComponentMap,
  selectedElementId: string,
  currentComponentType: Plugins.QualifiedElement
) => {
  const componentTypesToCheck = [
    /**
     * If a component is selected on the Canvas (but not focused), then the selectedElementId
     * will be the root of the component, which lives inside of the componentMap.
     */
    qualify(Plugins.name('__SitePlugin'), Component.name(selectedElementId)),
    /**
     * If the selected element matches here, it means we're focused inside of a component.
     */
    currentComponentType,
  ];

  for (const type of componentTypesToCheck) {
    const currentComponent = componentMap.get(type);
    if (currentComponent) {
      return {componentType: type, currentComponent};
    }
  }

  /**
   * If nothing else matched, we're just in the children of the page component.
   */
  return {
    componentType: pageComponentType,
    currentComponent: componentMap.get(pageComponentType),
  };
};

const updateElementWithTranslatedData = weakMemo(
  (translatedNodeDataByElementId: Record<string, TranslatedNodeData[]>) =>
    weakMemo(
      (
        normalizeElement: (
          el: ExpressionElement,
          seedId: string
        ) => ExpressionElement
      ) =>
        matchExpression({
          Element: (el) => {
            const translatedDataForThisElement:
              | TranslatedNodeData[]
              | undefined = translatedNodeDataByElementId[el.id];

            if (!translatedDataForThisElement) {
              return EElement(el);
            }

            translatedDataForThisElement.forEach((translatedData) => {
              const {path} = translatedData;
              if (translatedData.type === 'whole-text') {
                const hasNestedTags = translatedData.v.includes('<n ');

                if (hasNestedTags) {
                  const tokens = parseTextWithTags(translatedData.v);
                  const newChildren = buildChildrenFromTokens(tokens, el);

                  el = Expressions.setElementData(
                    el,
                    Expressions.sparseSetIn(
                      Expressions.getElementData(el),
                      [{in: 'Record', at: 'children'}],
                      EList(newChildren)
                    )
                  );
                  el = normalizeElement(el, generateGuid());
                } else {
                  const children = splitTextIntoElementsWithLineBreaks(
                    translatedData.v
                  );

                  el = Expressions.setElementData(
                    el,
                    Expressions.sparseSetIn(
                      Expressions.getElementData(el),
                      [{in: 'Record', at: 'children'}],
                      EList(children)
                    )
                  );
                }
              } else if (translatedData.type === 'element-attribute' && path) {
                el = Expressions.setElementData(
                  el,
                  Expressions.sparseSetIn(
                    Expressions.getElementData(el),
                    path,
                    EText(translatedData.v)
                  )
                );
              } else if (
                translatedData.type === 'attr-property-override' &&
                translatedData.fieldId
              ) {
                // Case of an attribute property override in a component instance
                el = Expressions.setElementData(
                  el,
                  Expressions.sparseSetIn(
                    Expressions.getElementData(el),
                    [{in: 'Record', at: translatedData.fieldId}],
                    EText(translatedData.v)
                  )
                );
              } else {
                el = Expressions.setElementData(el, EText(translatedData.v));
              }
            });

            return EElement(el);
          },
          _: identity,
        })
    )
);

const groupTranslatedNodeDataByType = (
  acc: {
    translatedNodeDataByElementId: Record<string, TranslatedNodeData[]>;
    translatedPropertiesByFieldId: Record<string, TranslatedNodeData[]>;
  },
  translatedNodeData: TranslatedNodeData
) => {
  if (
    translatedNodeData.type === 'component-property' &&
    translatedNodeData.fieldId
  ) {
    if (!acc.translatedPropertiesByFieldId[translatedNodeData.fieldId]) {
      acc.translatedPropertiesByFieldId[translatedNodeData.fieldId] = [];
    }

    acc.translatedPropertiesByFieldId[translatedNodeData.fieldId]?.push(
      translatedNodeData
    );
  } else {
    if (!acc.translatedNodeDataByElementId[translatedNodeData._id]) {
      acc.translatedNodeDataByElementId[translatedNodeData._id] = [];
    }

    acc.translatedNodeDataByElementId[translatedNodeData._id]?.push(
      translatedNodeData
    );
  }
  return acc;
};

export const updateComponentMapWithTranslatedNodeData = (
  componentMap: Component.ComponentMap,
  componentTypeOfNodeBeingTranslated: Plugins.QualifiedElement,
  allTranslatedNodeData: TranslatedNodeData[],
  plugins: Plugins.PluginsType,
  addExpression: ({
    components,
    newExpression,
    key,
    element,
  }: {
    components: Component.ComponentMap;
    newExpression: Expression;
    key: ExpressionPath;
    element: ExpressionElement;
  }) => Component.ComponentMap,
  // Injected to avoid a circular dependency: importing ContentEditor directly from localization
  // chains through ContentEditorElementCreation → render/dynamo/commerce → localization.
  // Per https://github.com/webflow/webflow/blob/dev/.agentflow/rules/cms-authoring/forge-components-in-rtf.md?plain=1#L57
  // always use dependency injection for enforceTagStructure rather than a direct import.
  normalizeElement: (el: ExpressionElement, seedId: string) => ExpressionElement
) => {
  // Group translated node data by translatable parent element ID to reduce overall component map updates
  // A "translatable parent element ID" is typically the ID of the node that the user selected,
  // however if there was a Component as a child of the selected node, we need to reference
  // the root component instance so we can update the default values on properties of it.
  const translatedNodeDataByTranslatableParentId = allTranslatedNodeData.reduce<
    Record<string, TranslatedNodeData[]>
  >((acc, translatedNode) => {
    if (!acc[translatedNode.translatableParentId]) {
      acc[translatedNode.translatableParentId] = [];
    }

    acc[translatedNode.translatableParentId]?.push(translatedNode);

    return acc;
  }, {});

  return Object.entries(translatedNodeDataByTranslatableParentId).reduce(
    (
      nextComponentMap,
      [translatableParentId, translatedNodeDataForThisParent]
    ) => {
      const body = getBody(nextComponentMap);
      const translatableParent = body
        ? Expressions.getElementById(EElement(body), translatableParentId)
        : null;
      const translatableParentType = translatableParent?.type ?? null;

      if (
        translatableParentType &&
        getPluginName(translatableParentType) === Plugins.name('__SitePlugin')
      ) {
        const component = nextComponentMap.get(translatableParentType);
        const isLibraryComponent = component
          ? Boolean(Component.getOrigin(component))
          : false;

        if (component && translatableParent && body && isLibraryComponent) {
          /*
           * For Library Components, we want to add overrides for any text props that do not already
           * have an override on this site (in primary or in the current locale) with the translated value
           */
          const overridesToCreate = Expressions.everything<OverrideData[]>(
            concat
          )(
            matchExpression({
              Element: (element) => {
                if (!isComponent(nextComponentMap)(element)) {
                  return Expressions.resume(emptyArray);
                }

                const componentType = element.type;
                const componentRoot = nextComponentMap.get(componentType);

                if (!componentRoot) {
                  return Expressions.resume(emptyArray);
                }

                const componentProperties = Types.getRecordFields(
                  Component.getDataType(componentRoot)
                );

                if (
                  !Component.getOrigin(componentRoot) ||
                  componentProperties == null
                ) {
                  return Expressions.resume(emptyArray);
                }

                return Expressions.resume(
                  Object.entries(componentProperties).reduce(
                    (arr, [componentPropertyId, componentPropertyType]) => {
                      if (
                        Types.isTypeConstructor(componentPropertyType) &&
                        !Types.isRichTextChildrenType(componentPropertyType)
                      ) {
                        return arr;
                      }

                      const overridesValues = Expressions.getIn(
                        Expressions.getElementData(element),
                        [{in: 'Record', at: componentPropertyId}]
                      );

                      if (overridesValues) {
                        return arr;
                      }

                      let defaultValue = Types.getDefaultValue(
                        componentPropertyType,
                        null
                      );

                      if (!Array.isArray(defaultValue)) {
                        return arr;
                      }

                      if (Types.isRichTextChildrenType(componentPropertyType)) {
                        defaultValue = nodePojosToElements(
                          defaultValue,
                          plugins
                        );
                      } else if (
                        isGuid(defaultValue[0]?.id) &&
                        typeof defaultValue[0]?.data?.value === 'string'
                      ) {
                        defaultValue = EList([
                          EElement({
                            id: defaultValue[0].id,
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('String')
                            ),
                            data: EText(defaultValue[0].data.value),
                          }),
                        ]);
                      }

                      // update text value with translation
                      defaultValue = Expressions.everywhere(
                        matchExpression({
                          Element: (e) => {
                            const translatedOverride =
                              translatedNodeDataForThisParent.find(
                                ({_id}) => e.id === _id
                              );

                            if (translatedOverride) {
                              return EElement(
                                Expressions.setElementData(
                                  e,
                                  EText(translatedOverride.v)
                                )
                              );
                            }
                            return EElement(e);
                          },
                          _: identity,
                        })
                      )(defaultValue);

                      arr.push({
                        element,
                        componentPropertyId,
                        defaultValue,
                      });

                      return arr;
                    },
                    [] as OverrideData[]
                  )
                );
              },
              _: constant(Expressions.resume(emptyArray)),
            })
          )(EElement(translatableParent));

          for (const {
            defaultValue,
            componentPropertyId,
            element,
          } of overridesToCreate) {
            const key = [
              {in: 'Record', at: componentPropertyId},
            ] as ExpressionPath;

            if (Expressions.isList(defaultValue)) {
              nextComponentMap = addExpression({
                components: nextComponentMap,
                newExpression: defaultValue,
                key,
                element,
              });
            }
          }
        }
      }

      const {currentComponent, componentType} = findSelectedElementComponent(
        nextComponentMap,
        translatableParentId,
        componentTypeOfNodeBeingTranslated
      );

      if (!currentComponent || !componentType) {
        return nextComponentMap;
      }

      // Library components cannot have localized definitions and default property values,
      // so we need to skip any translations for these components.
      const isLibraryComponent = Boolean(Component.getOrigin(currentComponent));
      if (isLibraryComponent) {
        return nextComponentMap;
      }

      // Once we're inside of a parent element, we can group the translated nodes by their type
      // so we can do faster lookups later
      const {translatedNodeDataByElementId, translatedPropertiesByFieldId} =
        translatedNodeDataForThisParent.reduce(groupTranslatedNodeDataByType, {
          translatedNodeDataByElementId: {},
          translatedPropertiesByFieldId: {},
        });

      const dataType = Component.getDataType(currentComponent);
      const componentFields = Types.getRecordFields(dataType);
      if (componentFields) {
        let nextDataType = dataType;

        const originalDefaultData = Component.getDefaultData(currentComponent);
        const originalDefaultDataRecord = originalDefaultData
          ? Expressions.getFunction(originalDefaultData)?.body
          : undefined;
        let nextDefaultDataRecord = originalDefaultDataRecord;

        for (const [fieldKey, fieldType] of Object.entries(componentFields)) {
          const translatedElements = translatedPropertiesByFieldId[fieldKey];
          if (!translatedElements) {
            continue;
          }

          let expression: Expression | undefined;

          if (Types.isAltTextType(fieldType)) {
            // Alt attributes can only be a single item, so we can just take the first one.
            const firstElement = translatedElements[0];
            if (firstElement) {
              expression = EText(firstElement.v);
            }
          } else if (Types.isListOfElements(fieldType)) {
            const defaultExpression = getFieldDefaultExpression(
              currentComponent,
              fieldKey,
              fieldType,
              plugins
            );
            const translatedElementMap = translatedElements.reduce<
              Record<string, TranslatedNodeData>
            >((acc, elementData) => {
              acc[elementData._id] = elementData;
              return acc;
            }, {});

            expression = Expressions.everywhere(
              matchExpression({
                Element: (e) => {
                  const translatedProperty = translatedElementMap[e.id];
                  if (translatedProperty) {
                    return EElement(
                      Expressions.setElementData(e, EText(translatedProperty.v))
                    );
                  }
                  return EElement(e);
                },
                _: identity,
              })
            )(defaultExpression);
          }

          if (expression) {
            nextDataType = Types.updateRecordField(
              nextDataType,
              fieldKey,
              (field) => {
                const evaluated = Expressions.evaluate(
                  Plugins.getEvalEnv(plugins),
                  expression
                );
                return withSystem(
                  {...getSystem(field), defaultValue: evaluated},
                  field
                );
              }
            );
            if (nextDefaultDataRecord) {
              nextDefaultDataRecord = Expressions.sparseSetIn(
                nextDefaultDataRecord,
                [{in: 'Record', at: fieldKey}],
                expression
              );
            }
          }
        }

        if (
          nextDataType !== dataType ||
          nextDefaultDataRecord !== originalDefaultDataRecord
        ) {
          nextComponentMap = nextComponentMap.set(
            componentType,
            Component.update(currentComponent, {
              dataType: nextDataType,
              defaultData: nextDefaultDataRecord
                ? EFunction({param: 'context', body: nextDefaultDataRecord})
                : undefined,
            })
          );
        }
      }

      /**
       * NOTE: By using `everywhere` instead of looping through and using `updateElement`,
       * we can optimize a bunch of unnecessary traversals since updateElement will
       * iterate through every Element in the currentComponent for each of the translated nodes.
       * In this approach, we iterate through the currentComponent tree _once_, and just match elements
       * to the translated values if they exist.
       */
      const nextRender = Expressions.everywhere(
        updateElementWithTranslatedData(translatedNodeDataByElementId)(
          normalizeElement
        )
      )(Component.getRender(currentComponent));

      return updateComponentRenderInComponentMap(
        nextComponentMap,
        componentType,
        nextRender
      );
    },
    componentMap
  );
};
